import moment from "moment"
import { isStaffInvitationReady } from "../../utils/prospect/backgroundCheckCta"

// CCMS background check statuses (Membership::Prospect::GetBackgroundCheck)
// that mean a screening is underway but has no result yet.
const BACKGROUND_CHECK_PENDING_STATUSES = new Set([
  "IN_PROGRESS",
  "IN_REVIEW",
  "DOCUMENTATION_NEEDED",
])

export const BACKGROUND_CHECK_VALID_FOR_YEARS = 1

export const BACKGROUND_CHECK_STEP_STATE = {
  APPROVED: "approved",
  IN_PROGRESS: "in_progress",
  DENIED: "denied",
  EXPIRED: "expired",
  NOT_STARTED: "not_started",
}

// Reinstatement needs a check screened less than a year ago. CCMS only flips
// APPROVED to EXPIRED the day after the anniversary and never expires an
// approval that has no screening date, so the clock is applied here.
export const backgroundCheckExpiresAt = screenedAt =>
  screenedAt
    ? moment(screenedAt).add(BACKGROUND_CHECK_VALID_FOR_YEARS, "year")
    : null

// The latest HireRight order stays on the prospect for a year after it
// completes, so a Completed order only counts while it is newer than the CCMS
// screening (staff have not recorded its result yet).
const isOrderOpen = (order, screenedAt) => {
  if (!order || order.status === "Canceled") return false
  if (order.status === "Completed") {
    return !screenedAt || moment(order.createdAt).isAfter(screenedAt)
  }
  return Boolean(
    order.awaitingModeration || order.paidAt || isStaffInvitationReady(order),
  )
}

export const resolveBackgroundCheckStep = (
  { backgroundCheck, order } = {},
  now = moment(),
) => {
  const status = backgroundCheck?.status
  const expiresAt = backgroundCheckExpiresAt(backgroundCheck?.screenedAt)

  if (status === "APPROVED" && expiresAt?.isAfter(now)) {
    return {
      state: BACKGROUND_CHECK_STEP_STATE.APPROVED,
      complete: true,
      expiresAt: expiresAt.toISOString(),
    }
  }

  if (status === "DENIED") {
    return { state: BACKGROUND_CHECK_STEP_STATE.DENIED, complete: false }
  }

  const orderOpen = isOrderOpen(order, backgroundCheck?.screenedAt)
  if (orderOpen || BACKGROUND_CHECK_PENDING_STATUSES.has(status)) {
    return {
      state: BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS,
      complete: false,
      canContinue: orderOpen && !order.awaitingModeration,
    }
  }

  if (status === "APPROVED" || status === "EXPIRED") {
    return {
      state: BACKGROUND_CHECK_STEP_STATE.EXPIRED,
      complete: false,
      expiredAt: expiresAt?.toISOString() || backgroundCheck.expiredAt || null,
    }
  }

  return { state: BACKGROUND_CHECK_STEP_STATE.NOT_STARTED, complete: false }
}

export const REINSTATEMENT_PDR_COHORT_CODE = "RI"

export const PDR_STEP_STATE = {
  MET: "met",
  NOT_MET: "not_met",
  UNKNOWN: "unknown",
}

// pga-backend reports an RI row with nothing owed as no_requirements.
const PDR_MET_STATUSES = ["requirements_met", "no_requirements"]

const isCount = value => Number.isFinite(value) && value >= 0

const isBucket = bucket =>
  typeof bucket?.label === "string" &&
  bucket.label.trim() !== "" &&
  isCount(bucket.earned) &&
  isCount(bucket.totalRequirement)

// Staff record the reinstatement obligation (deficit already folded in) as the
// member's RI cycle row; without one, Membership Services has the requirement.
// Complete needs the backend status and every bucket to agree, so malformed
// buckets or a backend rule change never claim the requirement is met. Earned
// buckets under a not-met status leave nothing to list, so that disagreement
// goes to Membership Services too.
export const resolvePdrStep = pdrRequirements => {
  const unknown = {
    state: PDR_STEP_STATE.UNKNOWN,
    complete: false,
    requirements: [],
  }
  const requirements = pdrRequirements?.requirements
  if (
    pdrRequirements?.cohortCode !== REINSTATEMENT_PDR_COHORT_CODE ||
    !Array.isArray(requirements) ||
    !requirements.every(isBucket) ||
    (requirements.length === 0 && pdrRequirements.status !== "no_requirements")
  ) {
    return unknown
  }

  const buckets = requirements.map(({ label, earned, totalRequirement }) => ({
    label,
    earned,
    required: totalRequirement,
    remaining: Math.max(totalRequirement - earned, 0),
  }))
  const bucketsMet = buckets.every(bucket => bucket.remaining === 0)
  const statusMet = PDR_MET_STATUSES.includes(pdrRequirements.status)
  if (bucketsMet && !statusMet) {
    return unknown
  }
  const met = bucketsMet && statusMet

  return {
    state: met ? PDR_STEP_STATE.MET : PDR_STEP_STATE.NOT_MET,
    complete: met,
    requirements: buckets,
  }
}
